/** Calculates a normalized danger score from nearby enemy aggro levels with distance falloff and sigmoid scaling. */
import type { Enemy } from '../entities/Enemy';
import { clamp, distance } from '../utils/Math';

/**
 * Calculate danger level (0-1) based on nearby enemies, their aggro, and distance.
 * Called on-demand (skill activation + XP award), not per-frame.
 *
 * Each on-screen enemy contributes max(presenceBase, aggro) scaled by a
 * cosine distance falloff from the camera center (≈ player position).
 *
 * The linear sum is passed through a sigmoid to weight enemy presence
 * nonlinearly: low presence climbs steeply, high presence saturates.
 */
export function calculateDanger(
  enemies: readonly Enemy[],
  camera: Phaser.Cameras.Scene2D.Camera,
  maxEnemies: number,
  presenceBase: number = 0,
  distanceFalloffRadius: number = 500,
  distanceFloor: number = 0.05,
  sigmoidSteepness: number = 8,
  sigmoidMidpoint: number = 0.18,
): number {
  const view = camera.worldView;
  const left = view.x;
  const right = left + view.width;
  const top = view.y;
  const bottom = top + view.height;

  // Camera center ≈ player position (camera follows player)
  const cx = left + view.width / 2;
  const cy = top + view.height / 2;

  let total = 0;
  for (const enemy of enemies) {
    const x = enemy.sprite.x;
    const y = enemy.sprite.y;
    if (x < left || x > right || y < top || y > bottom) continue;

    const contribution = Math.max(presenceBase, enemy.ai.aggro);
    const dist = distance(cx, cy, x, y);
    const distFactor = dangerDistanceFalloff(dist, distanceFalloffRadius, distanceFloor);
    total += contribution * distFactor;
  }

  const linear = clamp(total / (maxEnemies * 100), 0, 1);
  return dangerSigmoid(linear, sigmoidSteepness, sigmoidMidpoint);
}

/**
 * Sigmoid rescaled to map [0,1] → [0,1] with the steepest climb at the midpoint.
 * Low presence ramps quickly; high presence saturates.
 */
export function dangerSigmoid(x: number, k: number, midpoint: number): number {
  const raw = 1 / (1 + Math.exp(-k * (x - midpoint)));
  const min = 1 / (1 + Math.exp(-k * (0 - midpoint)));
  const max = 1 / (1 + Math.exp(-k * (1 - midpoint)));
  return (raw - min) / (max - min);
}

/**
 * Cosine-based distance falloff: smooth curve from 1.0 at dist=0 to floor at dist=radius.
 * C1 continuous — no sudden jumps or slope changes.
 */
export function dangerDistanceFalloff(dist: number, radius: number, floor: number): number {
  const t = clamp(dist / radius, 0, 1);
  return floor + (1 - floor) * (Math.cos(Math.PI * t) * 0.5 + 0.5);
}
